import { ProductApiModel } from '../../models/ProductModel';
import emptySliceApi from '../../store/emptyApiSlice';
import { User } from '../../models/UserModels';

export interface DealUserInvite {
  dealUserRequestJson: string;
  csvFile?: string | Blob;
  domainsCsvFile?: string | Blob;
  excludedEmailsCsvFile?: string | Blob;
}

export interface AddDomain {
  name: string;
  blacklist: string[];
}

export interface SummaryUserInvite {
  AccountNumber: string;
  IsDataShareRequired: boolean;
  CreatedByPersonId: number;
  SendInvite: boolean;
  contacts: {
    email: string;
    phone: {
      countryCodeId: string;
      countryCode: string;
      number: string;
    };
  }[];
  domains: AddDomain[];
  ProductAssignment: number[];
}

export interface CreateAssignmentRequest {
  modifiedByPersonId: number;
  modifiedByPersonFullName: string;
  userIds: number[];
  accountProductIds: number[];
}

export interface BulkAssignmentResponse {
  tottal: number;
  succeeded: ProductApiModel[];
  failed: ProductApiModel[];
}
export interface RemoveAssignmentRequest {
  modifiedByPersonId: number;
  modifiedByPersonFullName: string;
  userAssignmentIds: number[];
  agreementId?: string;
}
export interface BulkRestoreResponse {
  total: number;
  succeeded: number;
  failed: number;
}
export interface RestoreUser {
  userId: string;
  productAssignments: number[];
}
export interface BulkEnableUserProps {
  restoreUsers: RestoreUser[];
}

export const apiSlice = emptySliceApi.injectEndpoints({
  endpoints: (builder) => ({
    inviteUsers: builder.mutation<
      void,
      {
        accountId: string;
        currentUserId: string;
        dealId: string;
        emails: string[];
        products: number[];
        domains: AddDomain[];
      }
    >({
      query: ({
        accountId,
        dealId,
        emails,
        currentUserId,
        products,
        domains,
      }) => {
        const contacts = emails.map((email) => ({
          email,
          phone: null,
        }));
        const data: SummaryUserInvite = {
          AccountNumber: accountId,
          IsDataShareRequired: true,
          CreatedByPersonId: Number(currentUserId),
          SendInvite: true,
          contacts,
          domains,
          ProductAssignment: products,
        };
        const formData = new FormData();
        formData.append('requestJson', JSON.stringify(data));
        return {
          url: `v4/accounts/${accountId}/users/deals/${dealId}`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error) =>
        error ? [] : ['User', 'Domain', 'Deal', 'Group', 'Report'],
    }),
    summarizeInvitees: builder.mutation<
      {
        invalidFormEmails: string[];
        validFormEmails: string[];
        invalidCsvEmails: string[];
        validCsvEmails: string[];
        invalidCsvDomains: string[];
        validCsvDomains: string[];
        validCsvExcludedEmails: string[];
        invalidCsvExcludedEmails: string[];
      },
      { accountId: string; dealId: string; body: DealUserInvite; type: string }
    >({
      query: ({ accountId, dealId, body, type }) => {
        const formData = new FormData();
        formData.append(
          'dealUserRequestJson',
          body.dealUserRequestJson || '{}',
        );
        if (body.domainsCsvFile)
          formData.append('domainsCsvFile', body.domainsCsvFile);

        if (body.csvFile) formData.append('csvFile', body.csvFile);

        if (body.excludedEmailsCsvFile)
          formData.append('excludedEmailsCsvFile', body.excludedEmailsCsvFile);

        return {
          url: `accounts/${accountId}/deals/${dealId}/users/summarize?type=${type}`,
          method: 'POST',
          body: formData,
        };
      },
    }),
    getPublicDomains: builder.query<string[], void>({
      query: () => `options/publicDomains`,
    }),
    createAssignment: builder.mutation<
      BulkAssignmentResponse,
      CreateAssignmentRequest
    >({
      query: (payload) => ({
        url: 'v4/users/bulk/assignments/add',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['User'],
    }),
    removeAssignment: builder.mutation<void, RemoveAssignmentRequest>({
      query: (payload) => ({
        url: 'v4/users/bulk/assignments/remove',
        method: 'DELETE',
        body: payload,
      }),
      invalidatesTags: ['User'],
    }),
    restoreAssignments: builder.mutation<
      User,
      {
        accountId: string;
        accountUserId: number;
        productAssignments: number[];
      }
    >({
      query: ({ accountId, accountUserId, productAssignments }) => ({
        url: `v4/accounts/${accountId}/users/${accountUserId}/restore`,
        method: 'PUT',
        body: { productAssignments },
      }),
      invalidatesTags: ['User'],
    }),
    enableUserBulk: builder.mutation<
      BulkRestoreResponse,
      {
        accountId: string;
        data: BulkEnableUserProps;
      }
    >({
      query: ({ accountId, data }) => ({
        url: `v4/accounts/${accountId}/users/restore:bulk`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),
  }),
});

export const {
  useInviteUsersMutation,
  useSummarizeInviteesMutation,
  useGetPublicDomainsQuery,
  useCreateAssignmentMutation,
  useRemoveAssignmentMutation,
  useRestoreAssignmentsMutation,
  useEnableUserBulkMutation,
} = apiSlice;
